import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { founders } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CustomCursor from '../components/CustomCursor';
import AnimatedBackground from '../components/AnimatedBackground';
import { SEO } from '../components/SEO';

const ChiSiamoPage = () => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Main scroll progress for the entire page (desktop only)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });
  
  // Smooth spring animation for the line
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  
  // Line height grows as user scrolls
  const lineHeight = useTransform(smoothProgress, [0, 0.65], ["10vh", "100vh"]);
  
  // Line opacity fades out near the philosophy section
  const lineOpacity = useTransform(scrollYProgress, [0.55, 0.7], [1, 0]);

  return (
    <div ref={containerRef} className="min-h-screen bg-[#0a0a0a] relative">
      <SEO page="about" />
      <AnimatedBackground particlesOnly />
      <CustomCursor />
      <Navbar />
      
      {/* Animated Gold Line - DESKTOP ONLY */}
      {!isMobile && (
        <motion.div 
          className="fixed left-1/2 -translate-x-1/2 top-0 w-px pointer-events-none z-30"
          style={{ 
            height: lineHeight,
            opacity: lineOpacity,
            background: 'linear-gradient(to bottom, transparent 0%, #FFD700 10%, #FFD700 90%, transparent 100%)',
          }}
        />
      )}
      
      {/* Hero */}
      <section className="pt-40 pb-24 px-6 relative overflow-hidden z-10">
        {/* Background accent - static initial line */}
        <div className="absolute top-0 left-1/2 w-px h-40 bg-gradient-to-b from-[#FFD700]/50 to-transparent" />

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            className="text-center mb-24"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-8 block">
              {t('about.founders_label')}
            </span>
            
            <div className="overflow-hidden mb-4">
              <motion.h1
                className="font-display text-7xl md:text-8xl lg:text-9xl text-white leading-[0.85]"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease: [0.33, 1, 0.68, 1] }}
              >
                {t('about.title').split(' ')[0]}
              </motion.h1>
            </div>
            <div className="overflow-hidden">
              <motion.h1
                className="font-display text-7xl md:text-8xl lg:text-9xl leading-[0.85]"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.2, ease: [0.33, 1, 0.68, 1] }}
              >
                <span className="text-outline text-white">{t('about.title').split(' ').slice(1).join(' ') || t('about.title').split(' ')[1] || ''}</span>
              </motion.h1>
            </div>

            <motion.p
              className="text-white/80 text-lg font-light max-w-2xl mx-auto mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              {t('about.hero_description')}
            </motion.p>
          </motion.div>

          {/* Founders signatures - aligned to center line */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 max-w-4xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            {/* Alexandar - Left side */}
            <div className="text-center md:text-right md:pr-16 lg:pr-24">
              <p className="text-white/30 text-xs tracking-[0.3em] uppercase mb-4">{t('about.cofounder')}</p>
              <p className="font-serif italic text-white text-3xl md:text-4xl">
                Alexandar Posinak
              </p>
            </div>
            
            {/* Arianna - Right side */}
            <div className="text-center md:text-left md:pl-16 lg:pl-24">
              <p className="text-[#FFD700]/50 text-xs tracking-[0.3em] uppercase mb-4">{t('about.cofounder')}</p>
              <p className="font-serif italic text-white text-3xl md:text-4xl">
                Arianna Adriani
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Founders Detail */}
      <section className="py-24 px-6 relative z-10">
        <div className="max-w-7xl mx-auto relative z-10">
          {founders.map((founder, index) => {
            const founderKey = index === 0 ? 'alex' : 'arianna';
            return (
            <motion.div
              key={founder.id}
              className={`grid lg:grid-cols-2 gap-16 items-center mb-32 last:mb-0 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* Founder Image */}
              <div className={`order-1 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative aspect-[3/4] bg-white/[0.02] border border-white/10 overflow-hidden group">
                  {/* Actual founder image */}
                  <img 
                    src={index === 0 ? '/images/founder_alex.png' : '/images/founder_arianna.png'}
                    alt={founder.fullName}
                    className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                  />
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Gold accent */}
                  <div className="absolute bottom-0 left-0 w-1/3 h-1 bg-[#FFD700]" />
                  
                  {/* Number badge */}
                  <span className="absolute top-6 left-6 font-display text-6xl text-white/20">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className={`order-2 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-4 block">
                  {t(`about.founders.${founderKey}.role`)}
                </span>
                
                <h2 className="font-display text-5xl md:text-6xl text-white mb-8">
                  {founder.fullName}
                </h2>
                
                <div className="text-white/90 font-light leading-relaxed text-lg mb-10 space-y-4">
                  {t(`about.founders.${founderKey}.bio`).split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>

                {/* Skills */}
                <div className="mb-4">
                  <span className="text-[#FFD700] text-xs tracking-[0.3em] uppercase mb-4 block">{t('about.skills_label')}</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {t(`about.founders.${founderKey}.skills`, { returnObjects: true }).map((skill, i) => (
                    <span
                      key={i}
                      className="px-4 py-2 border border-white/10 text-white/70 text-xs tracking-wider uppercase hover:border-[#FFD700] hover:text-[#FFD700] transition-colors duration-300"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          )})}
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-32 px-6 relative overflow-hidden border-t border-white/10 z-10">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white mb-16">
              {t('about.philosophy_title_1')}
              <br />
              <span className="text-outline">{t('about.philosophy_title_2')}</span>
            </h2>

            <div className="space-y-6 mb-16">
              {t('about.philosophy_points', { returnObjects: true }).map((point, index) => (
                <motion.p
                  key={index}
                  className="font-serif italic text-white/85 text-2xl md:text-3xl"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                >
                  {point}
                </motion.p>
              ))}
            </div>

            <motion.div
              className="border-l-2 border-[#FFD700] pl-8 text-left inline-block"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <p className="font-display text-3xl md:text-4xl text-[#FFD700]">
                {t('about.philosophy_quote')}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChiSiamoPage;
